<script setup lang="ts">
import AttendanceSignatureRow from "./AttendanceSignatureRow.vue";
import EmptyState from "~/components/Common/EmptyState.vue";
import SimpleTable from "~/components/Common/Tables/SimpleTable.vue";
import { useAttendanceReview } from "~/composables/attendance/useAttendanceReview";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import type {
  IAttendanceParticipantOption,
  IAttendanceSignatureRow,
} from "~/utils/interfaces/attendance-review-interfaces";

const store = useAttendanceReviewStore();
const { signatureHeaders } = useAttendanceReview();

function onPick(row: IAttendanceSignatureRow, user: IAttendanceParticipantOption | null) {
  row.userId = user?.userId ?? null;
  row.displayValue = user?.displayValue ?? null;
  // The admin has ruled on the identity, so the model's guess — and its warning — no longer applies.
  row.nameMatch = null;
}
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-col gap-1">
      <!-- Which sheet this is. Absent until the session lookup answers, and if it never does. -->
      <span v-if="store.sessionLabel" class="body_demi text-primary-color">{{ store.sessionLabel }}</span>
      <span class="body_regular text-secondary-color">
        Vérifiez l'association de chaque signature relevée sur la feuille, puis ajustez le temps effectué si nécessaire.
      </span>
    </div>

    <!--
      A proposal can carry no line at all — the analysis read the session but none of its names. Said
      out loud rather than left as a table with a header and nothing under it, which reads as broken.
    -->
    <div v-if="!store.signatures.length" class="mt-4">
      <EmptyState icon="warning" message="Aucun nom n'a été relevé sur cette feuille d'émargement" />
    </div>

    <SimpleTable
      v-else
      :headers="signatureHeaders"
      :rows="store.signatures"
      track-by="key"
      :expand-first-column="false"
    >
      <template #row="{ row }">
        <AttendanceSignatureRow :row="row" @pick="onPick(row, $event)" @update-hours="row.attendedHours = $event" />
      </template>
    </SimpleTable>
  </div>
</template>
